import SettingsSection from '@components/ProjectSettings/SettingsSection';
import { getJwtRoles, useSession } from '@descope/react-sdk';
import {
	Box,
	Button,
	Divider,
	Tooltip,
	Typography,
	useTheme
} from '@mui/material';
import { useToast } from 'hooks';
import React, { FC, useCallback, useMemo } from 'react';
import { ITheme } from 'types/theme';
import { ToastSeverity } from 'types/toast';
import { useBoolean } from 'usehooks-ts';
import CompanySettingsSSODialog from './CompanySettingsSSODialog';

const DESCOPE_INC_ADMIN_ROLE = 'Tenant Admin';

const CompanySettingsSSOSection: FC<{
	currentCompany: string;
}> = ({ currentCompany }) => {
	const { addToast } = useToast();
	const theme = useTheme<ITheme>();
	const { value: open, setTrue, setFalse } = useBoolean();
	const { sessionToken } = useSession();
	const handleSuccess = useCallback(() => {
		addToast({
			severity: ToastSeverity.Success,
			content: intl.tr('company.sso.action.dialog.toast.success')
		});
		setFalse();
	}, [addToast, setFalse]);

	const handleError = useCallback(() => {
		addToast({
			severity: ToastSeverity.Error,
			content: intl.tr('company.sso.action.dialog.toast.error')
		});
		setFalse();
	}, [addToast, setFalse]);

	const canUseSSOSetup = useMemo(
		() =>
			(getJwtRoles(sessionToken, currentCompany) || []).includes(
				DESCOPE_INC_ADMIN_ROLE
			),
		[sessionToken, currentCompany]
	);
	const ssoButton = (
		<Box
			flexGrow={1}
			display="flex"
			justifyContent="center"
			data-testid="sso-setup-wrapper"
		>
			<Button
				variant="contained"
				size="medium"
				disabled={!canUseSSOSetup}
				data-testid="sso-setup-button"
				onClick={setTrue}
			>
				{intl.tr('company.sso.config.start')}
			</Button>
		</Box>
	);
	const buttonWrapper = !canUseSSOSetup ? (
		<Tooltip title={intl.tr('company.sso.config.start.disabled')}>
			{ssoButton}
		</Tooltip>
	) : (
		ssoButton
	);

	return (
		<>
			<Box pt={6}>
				<Divider />
			</Box>
			<SettingsSection title={intl.tr('company.sso.config.header')} divider>
				<Box
					flexDirection="row"
					display="flex"
					data-testid="company-settings-sso-setup"
					bgcolor={theme.palette.card.main}
					p={3}
					alignItems="center"
					justifyContent="flex-start"
				>
					<Box>
						<Typography variant="body1">
							{intl.tr('company.sso.config.description')}
						</Typography>
					</Box>
					{buttonWrapper}
					{open && (
						<CompanySettingsSSODialog
							currentCompany={currentCompany}
							onError={handleError}
							onSuccess={handleSuccess}
							onCancel={setFalse}
						/>
					)}
				</Box>
			</SettingsSection>
		</>
	);
};

export { DESCOPE_INC_ADMIN_ROLE };
export default CompanySettingsSSOSection;
